import { Component, OnInit } from '@angular/core';
import { AuthorizationService } from '../../../services/authorization.service';

interface ConfigItem {
  id: string;
  name: string;
  description: string;
  route: string;
  icon: string;
  permission: string;
  color: string;
  superAdminOnly?: boolean;
  danger?: boolean;
}

interface ConfigCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
  items: ConfigItem[];
}

@Component({
  selector: 'app-config-hub',
  templateUrl: './config-hub.component.html',
  styleUrls: ['./config-hub.component.scss']
})
export class ConfigHubComponent implements OnInit {
  categories: ConfigCategory[] = [];
  isSuperAdmin: boolean = false;

  constructor(private authorizationService: AuthorizationService) {}

  ngOnInit(): void {
    this.isSuperAdmin = this.authorizationService.isSuperAdmin();
    this.initializeCategories();
  }

  private initializeCategories(): void {
    this.categories = [
      {
        id: 'finance',
        name: 'Finanzas',
        icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
        color: 'emerald',
        items: [
          {
            id: 'budgets',
            name: 'Configuración de Presupuestos',
            description: 'Gestiona categorías y subcategorías de presupuesto',
            route: '/dashboard/config/budgets',
            icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z',
            permission: 'CONFIG_BUDGETS_READ',
            color: 'emerald'
          },
          {
            id: 'banks',
            name: 'Bancos',
            description: 'Administra cuentas bancarias y entidades financieras',
            route: '/dashboard/config/banks',
            icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
            permission: 'CONFIG_BANKS_READ',
            color: 'emerald'
          },
          {
            id: 'payment-methods',
            name: 'Métodos de Pago',
            description: 'Configura los métodos de pago disponibles',
            route: '/dashboard/config/payment-methods',
            icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
            permission: 'CONFIG_PAYMENT_METHODS_READ',
            color: 'emerald'
          },
          {
            id: 'fee-types',
            name: 'Tipos de Comisión',
            description: 'Gestiona los tipos de comisiones y descuentos',
            route: '/dashboard/config/fee-types',
            icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
            permission: 'CONFIG_FEE_TYPES_READ',
            color: 'emerald'
          },
          {
            id: 'exchange-rate',
            name: 'Tasas de Cambio',
            description: 'Configura las tasas de cambio de divisas',
            route: '/dashboard/config/exchange-rate',
            icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
            permission: 'CONFIG_EXCHANGE_READ',
            color: 'emerald'
          },
          {
            id: 'exchange-history',
            name: 'Histórico de Tasas',
            description: 'Consulta el historial de tasas de cambio',
            route: '/dashboard/config/exchange-rate/history',
            icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
            permission: 'CONFIG_EXCHANGE_HISTORY',
            color: 'emerald'
          }
        ]
      },
      {
        id: 'integrations',
        name: 'Integraciones',
        icon: 'M13 10V3L4 14h7v7l9-11h-7z',
        color: 'blue',
        items: [
          {
            id: 'bitrix',
            name: 'Webhooks Bitrix',
            description: 'Visualiza las solicitudes recibidas de Bitrix',
            route: '/dashboard/bitrix-requests',
            icon: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
            permission: 'INTEGRATIONS_BITRIX_READ',
            color: 'blue'
          },
          {
            id: 'wompi',
            name: 'Webhooks Wompi',
            description: 'Visualiza las notificaciones de pago de Wompi',
            route: '/dashboard/wompi-requests',
            icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
            permission: 'INTEGRATIONS_WOMPI_READ',
            color: 'blue'
          },
          {
            id: 'wompi-relay',
            name: 'Relay Webhooks Wompi',
            description: 'Configura el reenvío de webhooks a sistemas externos',
            route: '/dashboard/config/wompi-relay',
            icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
            permission: 'INTEGRATIONS_WOMPI_READ',
            color: 'blue'
          },
          {
            id: 'report-upload',
            name: 'Carga de Reportes',
            description: 'Sube los reportes diarios de Wompi y Plink',
            route: '/dashboard/report-upload',
            icon: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12',
            permission: 'INTEGRATIONS_WOMPI_READ',
            color: 'blue'
          },
          {
            id: 'conciliation',
            name: 'Conciliación de Reportes',
            description: 'Configura la conciliación automática de pagos',
            route: '/dashboard/config/conciliation',
            icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
            permission: 'CONFIG_EXCHANGE_WRITE',
            color: 'blue'
          },
          {
            id: 'uploaded-reports',
            name: 'Reportes Cargados',
            description: 'Consulta el historial de reportes Wompi y Plink',
            route: '/dashboard/config/uploaded-reports',
            icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
            permission: 'INTEGRATIONS_WOMPI_READ',
            color: 'blue'
          },
          {
            id: 'conciliation-logs',
            name: 'Logs de Conciliación',
            description: 'Historial de ejecuciones del proceso de conciliación',
            route: '/dashboard/config/conciliation-logs',
            icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
            permission: 'INTEGRATIONS_WOMPI_READ',
            color: 'blue'
          }
        ]
      },
      {
        id: 'security',
        name: 'Seguridad',
        icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
        color: 'purple',
        items: [
          {
            id: 'roles',
            name: 'Roles y Permisos',
            description: 'Administra los roles y permisos del sistema',
            route: '/dashboard/config/roles',
            icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
            permission: 'CONFIG_ROLES_READ',
            color: 'purple',
            superAdminOnly: true
          },
          {
            id: 'users',
            name: 'Usuarios',
            description: 'Gestiona los usuarios y sus roles asignados',
            route: '/dashboard/config/users',
            icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
            permission: 'CONFIG_ROLES_USER_ROLES',
            color: 'purple',
            superAdminOnly: true
          },
          {
            id: 'audit-logs',
            name: 'Logs de Auditoría',
            description: 'Consulta el registro de actividades del sistema',
            route: '/dashboard/config/audit-logs',
            icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
            permission: 'CONFIG_AUDIT_READ',
            color: 'purple'
          },
          {
            id: 'email-logs',
            name: 'Rastreo de Emails',
            description: 'Historial de todos los correos enviados por el sistema',
            route: '/dashboard/config/email-logs',
            icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
            permission: 'CONFIG_EMAILLOG_READ',
            color: 'purple'
          },
          {
            id: 'approvers',
            name: 'Aprobadores',
            description: 'Gestiona los usuarios autorizados para aprobar documentos',
            route: '/dashboard/config/approvers',
            icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
            permission: 'CONFIG_APPROVERS_READ',
            color: 'purple',
            superAdminOnly: true
          }
        ]
      },
      {
        id: 'system',
        name: 'Sistema',
        icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
        color: 'red',
        items: [
          {
            id: 'system-reset',
            name: 'Reseteo del Sistema',
            description: 'Reinicia datos y configuraciones del sistema',
            route: '/dashboard/config/system-reset',
            icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.232 16.5c-.77.833.192 2.5 1.732 2.5z',
            permission: 'CONFIG_SYSTEM_RESET',
            color: 'red',
            danger: true
          }
        ]
      }
    ];
  }

  hasPermission(permission: string): boolean {
    return this.authorizationService.hasPermission(permission);
  }

  canShowItem(item: ConfigItem): boolean {
    if (item.superAdminOnly && !this.isSuperAdmin) {
      return false;
    }
    if (this.isSuperAdmin) {
      return true;
    }
    return this.hasPermission(item.permission);
  }

  canShowCategory(category: ConfigCategory): boolean {
    // Una categoría se muestra si tiene al menos un item visible
    return category.items.some(item => this.canShowItem(item));
  }

  getColorClasses(color: string, type: 'bg' | 'text' | 'border' | 'hover'): string {
    const colorMap: Record<string, Record<string, string>> = {
      emerald: {
        bg: 'bg-emerald-500',
        text: 'text-emerald-600',
        border: 'border-emerald-200',
        hover: 'hover:bg-emerald-50'
      },
      blue: {
        bg: 'bg-blue-500',
        text: 'text-blue-600',
        border: 'border-blue-200',
        hover: 'hover:bg-blue-50'
      },
      purple: {
        bg: 'bg-purple-500',
        text: 'text-purple-600',
        border: 'border-purple-200',
        hover: 'hover:bg-purple-50'
      },
      red: {
        bg: 'bg-red-500',
        text: 'text-red-600',
        border: 'border-red-200',
        hover: 'hover:bg-red-50'
      }
    };
    return colorMap[color]?.[type] || '';
  }
}

